import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { loginSchema, type LoginInput } from "@ridereserv/shared/validation";
import { normalizePhoneToE164 } from "@ridereserv/shared/utils";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/authStore";
import { surfaceLinkedRides } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import type { AuthSession, User, CompanyMembershipInfo } from "@ridereserv/shared/types";

interface LocationState {
  from?: { pathname?: string };
}

interface SessionResponse {
  user: User;
  memberships: CompanyMembershipInfo[];
}

type SignInMode = "password" | "phone";
type PhoneStep = "enter-phone" | "enter-code";

/**
 * Checks if user needs to complete account verification.
 *
 * Verification is REQUIRED for all users:
 * - Drivers: Must verify before their application appears in admin queue
 * - Company Admins: Must verify before their application is reviewed
 * - Customers: Must verify for full platform access
 *
 * SMS-first (MFW-U2): EITHER verified channel proves the account — a confirmed
 * phone (the primary, mobile-first channel) or a confirmed email (the
 * fallback). Existing email-verified users with unverified phones stay
 * verified.
 *
 * This is a soft block - users can log in but are immediately redirected
 * to the verification page until they confirm a channel.
 *
 * @param user - The user object from session
 * @returns Whether verification is needed (true when no channel is verified)
 */
function needsVerification(user: User): boolean {
  return !user.emailVerified && !user.phoneVerified;
}

/**
 * Login page.
 * @returns The login form
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, memberships } = useAuthStore();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<SignInMode>("password");

  // Phone (SMS-OTP) sign-in state. Mirrors the mobile app: enter phone ->
  // Supabase texts a code -> verify -> session. shouldCreateUser:false so this
  // path can only ever sign in an EXISTING account (new riders use /register).
  const [phoneStep, setPhoneStep] = useState<PhoneStep>("enter-phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneNotice, setPhoneNotice] = useState<string | null>(null);
  const [phoneBusy, setPhoneBusy] = useState(false);

  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | null;
  const from = state?.from?.pathname;
  const verificationSuccess = searchParams.get("verified") === "true";

  /**
   * Compute the redirect destination based on user memberships.
   * Always uses role-based routing to ensure users land on the right dashboard.
   * @param userMemberships - The user's company memberships
   * @returns The path to redirect to after login
   */
  const getRedirectPath = (userMemberships: CompanyMembershipInfo[]): string => {
    // Admin/Manager: redirect to their first company dashboard
    const adminMembership = userMemberships.find((m) =>
      m.roles.some((role) => ["admin", "manager"].includes(role))
    );
    if (adminMembership) {
      return `/${adminMembership.companyId}/dashboard`;
    }

    // Driver: go to rides dashboard (upgrade CTAs shown there for free tier)
    const driverMembership = userMemberships.find((m) => m.roles.includes("driver"));
    if (driverMembership) {
      return "/account/rides";
    }

    // Default: personal account
    return "/account";
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect if already authenticated (e.g. direct navigation to /login while
  // logged in, or browser autofill restoring a session).
  // Guard against the onAuthStateChange race: if the form submission is still
  // in-flight, the Supabase SIGNED_IN event fires here before onSubmit has
  // fetched memberships — skipping the redirect lets onSubmit navigate with
  // the fully-populated store.
  useEffect(() => {
    if (isAuthenticated && !isSubmitting && !phoneBusy) {
      const redirectPath = from ?? getRedirectPath(memberships);
      void navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, isSubmitting, phoneBusy, navigate, from, memberships]);

  /**
   * Fetch the API session for a freshly-issued Supabase token, hydrate the auth
   * store, surface any auto-linked past rides, and route to the right surface.
   * Shared by the password and phone sign-in paths.
   * @param accessToken - The Supabase access token from the just-completed sign-in
   * @returns Whether the session was fetched and the user routed
   */
  const completeSignIn = async (accessToken: string): Promise<boolean> => {
    const response = await api.get<{ success: boolean; data: AuthSession }>(
      "/auth/session",
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.data.success) return false;

    const session = response.data.data;
    setAuth(session.user, session.memberships);
    surfaceLinkedRides(session);

    if (needsVerification(session.user)) {
      void navigate("/verify", {
        replace: true,
        state: {
          email: session.user.email,
          phone: session.user.phone,
          fromRegistration: false,
          reason: "unconfirmed_login",
        },
      });
      return true;
    }

    void navigate(getRedirectPath(session.memberships), { replace: true });
    return true;
  };

  const onSubmit = async (data: LoginInput) => {
    setError(null);

    try {
      // Sign in with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (authError) {
        if (authError.message.toLowerCase().includes("email not confirmed")) {
          // The only sensible next action is verification, so go straight to
          // /verify — the carried `reason` makes the verify page explain why
          // the user landed there (never a silent redirect). UIR-AU6.
          void navigate("/verify", {
            state: {
              email: data.email,
              fromRegistration: false,
              reason: "unconfirmed_login",
            },
          });
          return;
        }
        setError(authError.message);
        return;
      }

      // Supabase signInWithPassword returns session when no error
      const { session } = authData;

      // Fetch user session data from our API
      const response = await api.get<{ success: boolean; data: SessionResponse }>(
        "/auth/session",
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.data.success) {
        const { user, memberships } = response.data.data;
        setAuth(user, memberships);

        // Check if user needs to verify their account. Carrying the phone lets
        // the verify page open in SMS mode (the primary channel); the reason
        // renders an explanatory banner so the redirect is never silent.
        if (needsVerification(user)) {
          void navigate("/verify", {
            replace: true,
            state: {
              email: user.email,
              phone: user.phone,
              fromRegistration: false,
              reason: "unconfirmed_login",
            },
          });
          return;
        }

        void navigate(getRedirectPath(memberships), { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(`Login failed: ${message}`);
    }
  };

  /**
   * Switch the sign-in method, clearing any in-flight phone state/errors.
   * @param next - The sign-in method to switch to
   */
  const switchMode = (next: SignInMode) => {
    setMode(next);
    setError(null);
    setPhoneError(null);
    setPhoneNotice(null);
    setPhoneStep("enter-phone");
    setCode("");
  };

  /** Step 1: ask Supabase to text a 6-digit code to an EXISTING account's phone. */
  const sendPhoneCode = async () => {
    setPhoneError(null);
    setPhoneNotice(null);
    const e164 = normalizePhoneToE164(phone);
    if (!e164) {
      setPhoneError("Enter a valid phone number, including area code.");
      return;
    }
    setPhoneBusy(true);
    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: e164,
        options: { shouldCreateUser: false },
      });
      if (otpError) {
        const retry = /after (\d+) second/.exec(otpError.message);
        // Uniform messaging: never reveal whether an account exists for a number.
        setPhoneError(
          retry?.[1]
            ? `Please wait ${retry[1]} seconds before requesting another code.`
            : "We couldn't send a code. Check the number and try again."
        );
        return;
      }
      setPhoneStep("enter-code");
      setPhoneNotice("If an account exists for that number, we've texted a 6-digit code.");
    } catch {
      setPhoneError("We couldn't send a code. Please try again.");
    } finally {
      setPhoneBusy(false);
    }
  };

  /** Step 2: verify the SMS code, then hydrate the session and route. */
  const verifyPhoneCode = async () => {
    setPhoneError(null);
    const e164 = normalizePhoneToE164(phone);
    if (!e164) {
      setPhoneError("Enter a valid phone number, including area code.");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setPhoneError("Enter the 6-digit code we texted you.");
      return;
    }
    setPhoneBusy(true);
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        phone: e164,
        token: code,
        type: "sms",
      });
      if (verifyError || !data.session) {
        setPhoneError(
          verifyError?.message.toLowerCase().includes("expired")
            ? "That code has expired or is incorrect. Request a new one."
            : "Verification failed. Check the code and try again."
        );
        return;
      }
      await completeSignIn(data.session.access_token);
    } catch (err) {
      console.error("Phone sign-in error:", err);
      setPhoneError("Sign-in failed. Please try again.");
    } finally {
      setPhoneBusy(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="bg-surface">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Welcome back</CardTitle>
          <CardDescription>Sign in to your account to continue</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Method toggle: password (default) or phone SMS code. */}
          <div
            className="mb-4 grid grid-cols-2 gap-1 rounded-md border border-neutral-200 p-1"
            role="tablist"
            aria-label="Sign-in method"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "password"}
              onClick={() => { switchMode("password"); }}
              className={`min-h-11 rounded-sm text-sm font-medium transition-colors ${
                mode === "password"
                  ? "bg-primary-600 text-white"
                  : "text-neutral-600 hover:text-foreground"
              }`}
            >
              Email &amp; password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "phone"}
              onClick={() => { switchMode("phone"); }}
              className={`min-h-11 rounded-sm text-sm font-medium transition-colors ${
                mode === "phone"
                  ? "bg-primary-600 text-white"
                  : "text-neutral-600 hover:text-foreground"
              }`}
            >
              Phone
            </button>
          </div>

          {verificationSuccess && (
            <Alert variant="default" className="mb-4 border-success-100 bg-success-50 text-success-700">
              <AlertDescription>
                Your account has been verified! Please sign in to continue.
              </AlertDescription>
            </Alert>
          )}

          {mode === "password" ? (
            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  error={!!errors.email}
                  {...register("email")}
                />
              </FormField>

              <FormField label="Password" htmlFor="password" required error={errors.password?.message}>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  error={!!errors.password}
                  {...register("password")}
                />
              </FormField>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" className="w-full" isLoading={isSubmitting}>
                Sign in
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              {phoneError && (
                <Alert variant="destructive">
                  <AlertDescription>{phoneError}</AlertDescription>
                </Alert>
              )}
              {phoneNotice && (
                <Alert variant="default" className="border-success-100 bg-success-50 text-success-700">
                  <AlertDescription>{phoneNotice}</AlertDescription>
                </Alert>
              )}

              <FormField label="Phone number" htmlFor="phone" required>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 555-1234"
                  value={phone}
                  disabled={phoneStep === "enter-code"}
                  onChange={(e) => { setPhone(e.target.value); }}
                />
              </FormField>

              {phoneStep === "enter-phone" ? (
                <Button
                  type="button"
                  className="w-full"
                  isLoading={phoneBusy}
                  onClick={() => void sendPhoneCode()}
                >
                  Text me a code
                </Button>
              ) : (
                <>
                  <FormField label="6-digit code" htmlFor="code" required>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); }}
                    />
                  </FormField>
                  <Button
                    type="button"
                    className="w-full"
                    isLoading={phoneBusy}
                    onClick={() => void verifyPhoneCode()}
                  >
                    Verify code
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => void sendPhoneCode()}
                    disabled={phoneBusy}
                  >
                    Resend code
                  </button>
                </>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-neutral-600">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Create one
            </Link>
          </p>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
